// @ts-nocheck
/**
 * Backend Logs viewer — redesigned to obd3-gui pattern (FillPage + PageHeader
 * + bordered filter Card + shadcn DataTable). SUPERADMIN-only.
 * Preserves v1 quirks: Type dropdown has an empty option even though
 * `combined` is default, Reset returns timeRange to "5".
 */
import { useEffect, useRef, useState } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Filter, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { getApiCall } from '@/service/httpCall';
import { API_BACKEND_LOGS } from '@/service/url';
import { USER_ROLES } from '@/utilities/constant';
import { Storage } from '@/utilities/setting';
import { CalendarTimeInput } from '@/components/ui/calendar-inputs';

const timeOptions = [
  { label: 'Last 5 mins', value: '5' },
  { label: 'Last 15 mins', value: '15' },
  { label: 'Last 30 mins', value: '30' },
  { label: 'Last 45 mins', value: '45' },
  { label: 'Whole Day', value: 'day' },
  { label: 'Custom', value: 'custom' },
];

const logTypes = [
  'combined',
  'users',
  'outbound_campaigns',
  'numbers',
  'agents',
  'cdr_service',
  'billing_service',
  'report_download_service',
  'error_responses',
  'session_replay',
  // 'webrtc_sip',
  // crons (backend: shared/config/logger-config.js cron_* loggers)
  'postpaid_topup',
  'cron_break_exceed',
  'cron_demo_expiry_sweep',
  'cron_follow_up_reminder',
  'cron_system_health_alert',
  'cron_transcript_retention',
  'cron_yearly_report_table',
  'cron_db_backup',
  'cron_user_journey_cleanup',
  'cron_replay_session_stale_closer',
];

const levels = ['info', 'error', 'warn'];

const getTodayDate = () => new Date().toISOString().split('T')[0];
const getYesterdayDate = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
};

const levelToneClass = (level: string) => {
  switch (level) {
    case 'error':
      return 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300';
    case 'warn':
      return 'bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300';
    case 'info':
      return 'bg-sky-100 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300';
    default:
      return 'bg-muted/60 text-muted-foreground ring-border/60';
  }
};

const BackendLogsPage = () => {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = usePersistedState('backend-logs/BackendLogs:globalFilter', '');
  const [loggedInUserRole, setLoggedInUserRole] = useState<any>(null);
  // The date always opens on today; the other filters are kept.
  const [filters, setFilters] = usePersistedState<any>('backend-logs/BackendLogs:filters', {
    type: 'combined',
    date: getTodayDate(),
    level: '',
    search: '',
    timeRange: '5',
    fromTime: '',
    toTime: '',
  }, {
    onRestore: (saved) => ({ ...saved, date: getTodayDate() }),
  });
  const [appliedFilters, setAppliedFilters] = usePersistedState<any>('backend-logs/BackendLogs:appliedFilters', filters, {
    onRestore: (saved) => ({ ...saved, date: getTodayDate() }),
  });
  const [paging, setPaging] = usePersistedState('backend-logs/BackendLogs:paging', { pageIndex: 0, pageSize: 100 }, {
    // A new day means new rows: start from the first page.
    onRestore: (saved) => ({ ...saved, pageIndex: 0 }),
  });
  const [total, setTotal] = useState(0);
  const reqSeq = useRef(0);

  useEffect(() => {
    const rawUser = Storage.getItem('user');
    if (!rawUser) return;
    try {
      const user = JSON.parse(rawUser);
      setLoggedInUserRole(user.role);
    } catch (e) {
      console.error('Invalid JSON', e);
    }
  }, []);

  useEffect(() => {
    if (!loggedInUserRole) return;
    if (loggedInUserRole !== USER_ROLES.SUPERADMIN) return;
    fetchLogs();
  }, [loggedInUserRole, appliedFilters, paging]);

  const fetchLogs = () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    const offset = paging.pageIndex * paging.pageSize;
    const query = `?type=${appliedFilters.type}&date=${appliedFilters.date}&level=${appliedFilters.level}&search=${encodeURIComponent(appliedFilters.search || '')}&timeRange=${appliedFilters.timeRange}&fromTime=${appliedFilters.fromTime || ''}&toTime=${appliedFilters.toTime || ''}&page=${paging.pageIndex + 1}&limit=${paging.pageSize}`;

    getApiCall(`${API_BACKEND_LOGS}${query}`)
      .then((res: any) => {
        if (seq !== reqSeq.current) return;
        setLogs(
          (res?.rows || []).map((line: any, index: number) => ({
            id: offset + index + 1,
            log: line,
          })),
        );
        setTotal(Number(res?.total) || 0);
      })
      .catch((err: any) => {
        if (seq !== reqSeq.current) return;
        toast.error(friendlyApiError(err) || 'Failed to load logs');
      })
      .finally(() => {
        if (seq === reqSeq.current) setLoading(false);
      });
  };

  const applyFilters = (next: any) => {
    setAppliedFilters(next);
    setPaging((p) => ({ ...p, pageIndex: 0 }));
  };

  const resetFilters = () => {
    const defaults = {
      type: 'combined',
      date: getTodayDate(),
      level: '',
      search: '',
      timeRange: '5',
      fromTime: '',
      toTime: '',
    };
    setFilters(defaults);
    applyFilters(defaults);
    setGlobalFilter('');
  };

  if (loggedInUserRole && loggedInUserRole !== USER_ROLES.SUPERADMIN) {
    return (
      <FillPage>
        <PageHeader title="Backend Logs" />
        <div className="rounded-md border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          Access denied. Backend logs are visible to super-admins only.
        </div>
      </FillPage>
    );
  }

  const displayRows = globalFilter.trim()
    ? logs.filter((r: any) =>
        String(r.log).toLowerCase().includes(globalFilter.trim().toLowerCase()),
      )
    : logs;

  const columns: ColumnDef<any>[] = [
    {
      id: 'lineNo',
      header: '#',
      size: 60,
      enableSorting: false,
      cell: ({ row }: any) => (
        <span className="font-mono text-xs text-muted-foreground">
          {row.original.id}
        </span>
      ),
    },
    {
      accessorKey: 'log',
      header: 'Log',
      cell: ({ row }: any) => {
        const line = row.original.log;
        const match = line.match(/(\d{2}:\d{2}:\d{2})/);
        if (!match) {
          return (
            <span className="whitespace-pre-wrap font-mono text-xs">
              {line}
            </span>
          );
        }
        const time = match[0];
        const parts = line.split(time);
        return (
          <span className="whitespace-pre-wrap font-mono text-xs">
            {parts[0]}
            <span className="mx-0.5 rounded-md bg-primary/10 px-1.5 py-0.5 font-bold italic text-primary ring-1 ring-inset ring-primary/25">
              {time}
            </span>
            {parts[1]}
          </span>
        );
      },
    },
  ];

  return (
    <FillPage>
      <PageHeader
        title="Backend Logs"
        description="Raw log viewer across all backend services — SUPERADMIN only."
      />

      {/* Filters */}
      <div
        className="mb-3 rounded-xl border border-border/60 bg-card px-3 py-2.5"
        style={{
          boxShadow:
            '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
        }}
      >
        <div className="flex flex-wrap items-end gap-2">
          <div className="w-[160px] space-y-0.5">
            <Label
              htmlFor="type"
              className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
            >
              Type
            </Label>
            <Select
              value={filters.type}
              onValueChange={(v) => setFilters({ ...filters, type: v })}
            >
              <SelectTrigger id="type" className="h-9 text-xs">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                {logTypes.map((t) => (
                  <SelectItem key={t} value={t} className="text-xs">
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-[130px] space-y-0.5">
            <Label
              htmlFor="level"
              className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
            >
              Level
            </Label>
            <Select
              value={filters.level || '__all__'}
              onValueChange={(v) =>
                setFilters({ ...filters, level: v === '__all__' ? '' : v })
              }
            >
              <SelectTrigger id="level" className="h-9 text-xs">
                <SelectValue placeholder="All levels" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__" className="text-xs">
                  All Levels
                </SelectItem>
                {levels.map((lvl) => (
                  <SelectItem key={lvl} value={lvl} className="text-xs">
                    <span
                      className={
                        'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset ' +
                        levelToneClass(lvl)
                      }
                    >
                      {lvl.toUpperCase()}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-[140px] space-y-0.5">
            <Label
              htmlFor="date"
              className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
            >
              Date
            </Label>
            <Select
              value={filters.date}
              onValueChange={(v) => setFilters({ ...filters, date: v })}
            >
              <SelectTrigger id="date" className="h-9 text-xs">
                <SelectValue placeholder="Date" />
              </SelectTrigger>
              <SelectContent>
                {[getTodayDate(), getYesterdayDate()].map((date) => (
                  <SelectItem key={date} value={date} className="text-xs">
                    {date}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-[150px] space-y-0.5">
            <Label
              htmlFor="timeRange"
              className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
            >
              Time Range
            </Label>
            <Select
              value={filters.timeRange}
              onValueChange={(v) =>
                setFilters({
                  ...filters,
                  timeRange: v,
                  fromTime: '',
                  toTime: '',
                })
              }
            >
              <SelectTrigger id="timeRange" className="h-9 text-xs">
                <SelectValue placeholder="Time" />
              </SelectTrigger>
              <SelectContent>
                {timeOptions.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    className="text-xs"
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {filters.timeRange === 'custom' && (
            <>
              <div className="w-[110px] space-y-0.5">
                <Label
                  htmlFor="fromTime"
                  className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
                >
                  From
                </Label>
                <CalendarTimeInput
                  id="fromTime"
                  value={filters.fromTime}
                  onChange={(e) =>
                    setFilters({ ...filters, fromTime: e })
                  }
                  className="input-embossed h-9 w-full rounded-md px-2.5 text-xs"
                />
              </div>
              <div className="w-[110px] space-y-0.5">
                <Label
                  htmlFor="toTime"
                  className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
                >
                  To
                </Label>
                <CalendarTimeInput
                  id="toTime"
                  value={filters.toTime}
                  onChange={(e) =>
                    setFilters({ ...filters, toTime: e })
                  }
                  className="input-embossed h-9 w-full rounded-md px-2.5 text-xs"
                />
              </div>
            </>
          )}
          <div className="min-w-[180px] flex-1 space-y-0.5">
            <Label
              htmlFor="search"
              className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
            >
              Server search
            </Label>
            <input
              id="search"
              placeholder="Search text sent to endpoint…"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="input-embossed h-9 w-full rounded-md px-2.5 text-xs"
            />
          </div>
          <div className="min-w-[160px] flex-1 space-y-0.5">
            <Label
              htmlFor="quick"
              className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
            >
              Quick Filter
            </Label>
            <input
              id="quick"
              placeholder="Filter loaded rows…"
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="input-embossed h-9 w-full rounded-md px-2.5 text-xs"
            />
          </div>
          <Button
            size="sm"
            disabled={loading}
            onClick={() => applyFilters({ ...filters })}
            className="gap-1.5"
          >
            <Filter className="h-3.5 w-3.5" />
            Apply
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={resetFilters}
            className="gap-1.5"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset
          </Button>
        </div>
      </div>

      <DataTable
        tableId="backend-logs:list:v1"
        columns={columns}
        data={displayRows}
        isLoading={loading}
        pageSize={100}
        serverPagination={{ ...paging, rowCount: total, onChange: setPaging }}
        emptyTitle="No logs found"
        emptyDescription="No log entries match the current filters."
        getRowId={(row: any) => String(row.id)}
        disableExport
      />
    </FillPage>
  );
};

export default BackendLogsPage;
